<template>
  <div class="schematic-preview">
    <div class="schematic-preview__header">
      <h4>Page preview</h4>
      <el-select
        :value="activeStep"
        class="step-selector"
        size="mini"
        @change="$emit('step-selected', $event)"
      >
        <el-option
          v-for="(step, index) in stepOptions"
          :key="index"
          :label="step.label"
          :value="index"
        ></el-option>
      </el-select>
    </div>

    <div class="schematic-preview__canvas" :style="canvasStyle">
      <!-- Header Module - Logo and Title -->
      <div v-if="showHeader" class="site-header">
        <img v-if="template.logo && template.show_logo"
             :src="template.logo"
             alt="Logo"
             :style="{ height: (template.logo_height || 60) + 'px', width: template.logo_width ? template.logo_width + 'px' : 'auto', objectFit: template.logo_fit || 'contain' }" />
        <div v-if="template.title" class="site-header__title" :style="{ fontFamily: template.font_family }">{{ template.title }}</div>
        <div v-if="template.subtext" class="site-header__subtext" :style="{ fontFamily: template.font_family }">{{ template.subtext }}</div>
      </div>

      <!-- Wizard Progress Bar -->
      <div v-if="template.display_progress_bar && activeStep >= 1 && activeStep <= 5" class="schematic-module module-progress">
        <div class="progress-steps">
          <div class="progress-step" :class="{ active: activeStep === 1, completed: activeStep > 1 }">
            <span class="step-icon">
              <i class="tim-icons icon-components"></i>
            </span>
            <span v-if="template.display_section_titles" class="step-label">Experience</span>
          </div>
          <div class="progress-line" :class="{ filled: activeStep > 1 }"></div>
          <div class="progress-step" :class="{ active: activeStep === 2, completed: activeStep > 2 }">
            <span class="step-icon">
              <i class="tim-icons icon-calendar-60"></i>
            </span>
            <span v-if="template.display_section_titles" class="step-label">Details</span>
          </div>
          <div class="progress-line" :class="{ filled: activeStep > 2 }"></div>
          <div class="progress-step" :class="{ active: activeStep === 3, completed: activeStep > 3 }">
            <span class="step-icon">
              <i class="tim-icons icon-single-02"></i>
            </span>
            <span v-if="template.display_section_titles" class="step-label">Info</span>
          </div>
          <div class="progress-line" :class="{ filled: activeStep > 3 }"></div>
          <div class="progress-step" :class="{ active: activeStep === 4, completed: activeStep > 4 }">
            <span class="step-icon">
              <i class="tim-icons icon-cart"></i>
            </span>
            <span v-if="template.display_section_titles" class="step-label">Payment</span>
          </div>
          <div class="progress-line" :class="{ filled: activeStep > 4 }"></div>
          <div class="progress-step" :class="{ active: activeStep === 5, completed: activeStep > 5 }">
            <span class="step-icon">
              <i class="tim-icons icon-check-2"></i>
            </span>
            <span v-if="template.display_section_titles" class="step-label">Review</span>
          </div>
        </div>
      </div>

      <!-- Step 0: General settings -->
      <div v-if="activeStep === 0" class="step-content">
        <div class="general-settings-preview">
          <!-- Header preview -->
          <div class="site-header">
            <img v-if="template.logo && template.show_logo"
                 :src="template.logo"
                 alt="Logo"
                 :style="{ height: (template.logo_height || 60) + 'px', width: template.logo_width ? template.logo_width + 'px' : 'auto', objectFit: template.logo_fit || 'contain' }" />
            <i v-if="!template.show_logo || !template.logo" class="tim-icons icon-image-02 site-header__placeholder-icon"></i>
            <div v-if="template.title" class="site-header__title" :style="{ fontFamily: template.font_family }">{{ template.title }}</div>
            <div v-if="template.subtext" class="site-header__subtext" :style="{ fontFamily: template.font_family }">{{ template.subtext }}</div>
            <div v-if="!template.title && !template.subtext && !template.show_logo" class="site-header__empty">Page header</div>
          </div>

          <!-- Progress bar indicator preview -->
          <div v-if="template.display_progress_bar" class="schematic-module module-progress">
            <div class="progress-steps">
              <div class="progress-step active">
                <span class="step-icon"><i class="tim-icons icon-components"></i></span>
                <span v-if="template.display_section_titles" class="step-label">Experience</span>
              </div>
              <div class="progress-line"></div>
              <div class="progress-step">
                <span class="step-icon"><i class="tim-icons icon-calendar-60"></i></span>
                <span v-if="template.display_section_titles" class="step-label">Details</span>
              </div>
              <div class="progress-line"></div>
              <div class="progress-step">
                <span class="step-icon"><i class="tim-icons icon-single-02"></i></span>
                <span v-if="template.display_section_titles" class="step-label">Info</span>
              </div>
              <div class="progress-line"></div>
              <div class="progress-step">
                <span class="step-icon"><i class="tim-icons icon-cart"></i></span>
                <span v-if="template.display_section_titles" class="step-label">Payment</span>
              </div>
              <div class="progress-line"></div>
              <div class="progress-step">
                <span class="step-icon"><i class="tim-icons icon-check-2"></i></span>
                <span v-if="template.display_section_titles" class="step-label">Review</span>
              </div>
            </div>
          </div>

          <!-- Color swatches -->
          <div class="settings-section">
            <div class="settings-section-title">Colors</div>
            <div class="color-swatches-grid">
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.background_color }"></div>
                <span class="color-swatch-label">Background</span>
              </div>
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.text_color }"></div>
                <span class="color-swatch-label">Text</span>
              </div>
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.card_color }"></div>
                <span class="color-swatch-label">Card</span>
              </div>
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.text_color_card }"></div>
                <span class="color-swatch-label">Card text</span>
              </div>
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.button_color }"></div>
                <span class="color-swatch-label">Button</span>
              </div>
              <div class="color-swatch-item">
                <div class="color-swatch" :style="{ backgroundColor: template.link_color }"></div>
                <span class="color-swatch-label">Link</span>
              </div>
            </div>
          </div>

          <!-- Typography -->
          <div class="settings-section">
            <div class="settings-section-title">Typography</div>
            <div class="font-preview-list">
              <div class="font-preview-item">
                <span class="font-preview-label">Page font</span>
                <span class="font-preview-name" :style="{ fontFamily: template.font_family }">{{ template.font_family || 'Roboto' }}</span>
              </div>
              <div class="font-preview-item">
                <span class="font-preview-label">Card font</span>
                <span class="font-preview-name" :style="{ fontFamily: template.font_family_card }">{{ template.font_family_card || 'Roboto' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 1: Event selection -->
      <div v-if="activeStep === 1" class="step-content">
        <!-- Date Navigation -->
        <div class="date-navigation">
          <span class="nav-arrow">&#9664; Prev</span>
          <div class="date-picker">
            <i class="tim-icons icon-calendar-60"></i>
            <span>February 6, 2026</span>
          </div>
          <span class="nav-arrow">Next &#9654;</span>
        </div>

        <!-- Card grid layout -->
        <div v-if="layoutName === 'cards'" class="events-layout events-cards">
          <div
            v-for="event in displayEvents"
            :key="event.id || event.game_id"
            class="event-card-schematic"
            :style="eventCardStyle"
          >
            <div class="event-image-placeholder">
              <img v-if="event.img" v-twin-src="event.img" :alt="event.name" />
              <i v-else class="tim-icons icon-controller"></i>
              <b-icon v-if="template.show_game_description && (template.game_info_trigger_type || 'icon') === 'icon'"
                      class="game-info-trigger-icon"
                      :icon="template.game_info_trigger_icon || 'info-circle'"
                      :style="{ color: template.button_color, ...infoTriggerPositionStyle }" />
              <span v-else-if="template.show_game_description" class="game-info-trigger-chip" :style="infoTriggerStyle">
                {{ template.game_info_trigger_text || 'Learn more' }}
              </span>
            </div>
            <div v-if="template.show_game_title" class="event-title">
              {{ truncateName(event.name) }}
            </div>
            <div class="event-meta" v-if="showEventMeta">
              <span v-if="template.show_number_of_players" class="meta-item">
                <i class="tim-icons icon-single-02"></i> 2-8
              </span>
              <span v-if="template.show_game_duration" class="meta-item">
                <i class="tim-icons icon-time-alarm"></i> 60m
              </span>
              <span v-if="template.show_game_difficulty" class="meta-item">
                <i class="tim-icons icon-chart-bar-32"></i>
              </span>
            </div>
            <div class="embedded-slots">
              <div class="slot" :style="cardSlotStyle">9:00</div>
              <div class="slot" :style="cardSlotStyle">10:30</div>
              <div class="slot disabled" :style="cardSlotStyle">12:00</div>
              <div class="slot" :style="cardSlotStyle">2:00</div>
              <div class="slot disabled" :style="cardSlotStyle">4:00</div>
              <div class="slot" :style="cardSlotStyle">6:00</div>
            </div>
            <div v-if="template.show_calendar_link" class="see-more-link">
              See more times &gt;
            </div>
          </div>

          <!-- Gift Card Card -->
          <div v-if="template.gift_cards_enabled" class="event-card-schematic gift-card-card" :style="eventCardStyle">
            <div class="event-image-placeholder gift-card-image">
              <i class="tim-icons icon-gift-2"></i>
            </div>
            <div class="event-title">Gift card</div>
            <div class="gift-card-description">Give the gift of adventure</div>
            <div class="gift-card-button" :style="buttonStyle">Purchase gift card</div>
          </div>
        </div>

        <!-- List board layout -->
        <div v-if="layoutName === 'list'" class="events-layout events-list">
          <div
            v-for="event in displayEvents"
            :key="'list-' + (event.id || event.game_id)"
            class="event-list-row"
            :class="{ 'event-list-row--img-right': template.list_image_position === 2 }"
            :style="{ ...eventCardStyle, flexDirection: template.list_image_position === 2 ? 'row-reverse' : 'row' }"
          >
            <div v-if="isListImageTop" class="event-list-left-col" :style="{ width: listThumbWidth + 'px' }">
              <div class="event-list-thumb" :style="{ width: listThumbWidth + 'px', height: listThumbHeight + 'px' }">
                <img v-if="event.img" v-twin-src="event.img" :alt="event.name" />
                <i v-else class="tim-icons icon-controller"></i>
              </div>
              <div class="event-list-info">
                <div class="event-list-info-content">
                  <span v-if="template.show_game_title !== false" class="event-list-name" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
                    {{ truncateName(event.name) }}
                  </span>
                  <div v-if="showEventMeta" class="event-list-meta" :style="{ color: template.text_color_card }">
                    <span v-if="template.show_number_of_players" class="meta-item">
                      <i class="tim-icons icon-single-02"></i> 2-8
                    </span>
                    <span v-if="template.show_game_duration" class="meta-item">
                      <i class="tim-icons icon-time-alarm"></i> 60m
                    </span>
                    <span v-if="template.show_game_difficulty" class="meta-item">
                      <i class="tim-icons icon-chart-bar-32"></i>
                    </span>
                  </div>
                  <span v-if="template.show_game_description" class="game-info-trigger-inline" :style="{ color: template.button_color }">
                    <b-icon v-if="(template.game_info_trigger_type || 'icon') === 'icon'" :icon="template.game_info_trigger_icon || 'info-circle'" font-scale="0.85" />
                    <template v-else>{{ template.game_info_trigger_text || 'Learn more' }}</template>
                  </span>
                </div>
              </div>
            </div>
            <template v-if="!isListImageTop">
              <div class="event-list-thumb" :style="{ width: listThumbWidth + 'px' }">
                <img v-if="event.img" v-twin-src="event.img" :alt="event.name" />
                <i v-else class="tim-icons icon-controller"></i>
              </div>
              <div class="event-list-info">
                <div class="event-list-info-content">
                  <span v-if="template.show_game_title !== false" class="event-list-name" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
                    {{ truncateName(event.name) }}
                  </span>
                  <div v-if="showEventMeta" class="event-list-meta" :style="{ color: template.text_color_card }">
                    <span v-if="template.show_number_of_players" class="meta-item">
                      <i class="tim-icons icon-single-02"></i> 2-8
                    </span>
                    <span v-if="template.show_game_duration" class="meta-item">
                      <i class="tim-icons icon-time-alarm"></i> 60m
                    </span>
                    <span v-if="template.show_game_difficulty" class="meta-item">
                      <i class="tim-icons icon-chart-bar-32"></i>
                    </span>
                  </div>
                  <span v-if="template.show_game_description" class="game-info-trigger-inline" :style="{ color: template.button_color }">
                    <b-icon v-if="(template.game_info_trigger_type || 'icon') === 'icon'" :icon="template.game_info_trigger_icon || 'info-circle'" font-scale="0.85" />
                    <template v-else>{{ template.game_info_trigger_text || 'Learn more' }}</template>
                  </span>
                </div>
              </div>
            </template>
            <div class="event-list-slots">
              <div class="slot" :style="listSlotStyle">9:00</div>
              <div class="slot" :style="listSlotStyle">10:30</div>
              <div class="slot" :style="listSlotStyle">12:00</div>
              <div class="slot" :style="listSlotStyle">2:00</div>
              <div class="slot disabled" :style="listSlotDisabledStyle">4:00</div>
              <div class="slot" :style="listSlotStyle">6:00</div>
            </div>
          </div>

          <!-- Gift cards row -->
          <div v-if="template.gift_cards_enabled" class="event-list-row"
               :class="{ 'event-list-row--img-right': template.list_image_position === 2 }"
               :style="{ ...eventCardStyle, flexDirection: template.list_image_position === 2 ? 'row-reverse' : 'row' }">
            <div v-if="isListImageTop" class="event-list-left-col" :style="{ width: listThumbWidth + 'px' }">
              <div class="event-list-thumb gift-card-thumb" :style="{ width: listThumbWidth + 'px', height: listThumbHeight + 'px' }">
                <i class="tim-icons icon-gift-2"></i>
              </div>
              <div class="event-list-info">
                <span class="event-list-name" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">Gift cards</span>
              </div>
            </div>
            <template v-if="!isListImageTop">
              <div class="event-list-thumb gift-card-thumb" :style="{ width: listThumbWidth + 'px' }">
                <i class="tim-icons icon-gift-2"></i>
              </div>
              <div class="event-list-info">
                <span class="event-list-name" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">Gift cards</span>
              </div>
            </template>
            <div class="event-list-slots">
              <div class="slot" :style="listSlotStyle">Purchase</div>
            </div>
          </div>
        </div>

        <!-- Calendar view layout -->
        <div v-if="layoutName === 'calendar'" class="events-layout events-calendar">
          <div class="event-card-schematic calendar-card" :style="eventCardStyle">
            <div class="calendar-card-row">
              <div class="calendar-poster">
                <img v-if="previewEvent && previewEvent.img" v-twin-src="previewEvent.img" :alt="previewEvent.name" />
                <i v-else class="tim-icons icon-controller"></i>
              </div>
              <div class="calendar-main">
                <div v-if="template.show_game_title" class="event-title">
                  {{ previewEvent ? truncateName(previewEvent.name) : 'Selected event' }}
                </div>
                <div class="calendar-weekdays">
                  <span v-for="(d, i) in ['S', 'M', 'T', 'W', 'T', 'F', 'S']" :key="'cwd-' + i">{{ d }}</span>
                </div>
                <div class="calendar-grid">
                  <div
                    v-for="n in 28"
                    :key="'cd-' + n"
                    class="calendar-day"
                    :class="{ 'calendar-day--muted': n < 6, 'calendar-day--selected': n === 13 }"
                    :style="n === 13 ? { background: template.button_color, borderColor: template.button_color, color: '#fff' } : {}"
                  >{{ n }}</div>
                </div>
                <div class="calendar-slots">
                  <div class="slot" :style="cardSlotStyle">1:00</div>
                  <div class="slot" :style="cardSlotStyle">2:00</div>
                  <div class="slot disabled" :style="cardSlotStyle">3:00</div>
                  <div class="slot" :style="cardSlotStyle">4:00</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Booking details -->
      <div v-if="activeStep === 2" class="step-content">
        <div class="booking-details-preview">
          <div class="selected-game-card" :style="eventCardStyle">
            <!-- Game image (full width on top) -->
            <div class="game-image-top">
              <img v-if="previewEvent && previewEvent.img" v-twin-src="previewEvent.img" :alt="previewEvent.name" />
              <i v-else class="tim-icons icon-controller"></i>
            </div>

            <div class="game-card-body">
              <!-- Game title -->
              <h5 v-if="template.show_game_title" class="game-title" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">{{ previewEvent ? previewEvent.name : 'Selected event' }}</h5>
              <p class="game-description" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }" v-html="previewEvent && previewEvent.description ? sanitizeQuillHtml(previewEvent.description) : 'Event description text goes here'"></p>

              <!-- Date/time + change link -->
              <div class="game-datetime" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
                <i class="tim-icons icon-calendar-60"></i> Fri 07 Feb 2026
                <i class="tim-icons icon-time-alarm ml-1"></i> 10:30
                <span class="change-link" :style="{ color: template.button_color }">Change</span>
              </div>

              <!-- Available tickets -->
              <div v-if="template.show_available_ticket_count" class="available-tickets" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
                6/8 available tickets
              </div>

              <!-- Pricing categories with +/- controls -->
              <div class="pricing-categories">
                <div class="pricing-category">
                  <label class="category-label" :style="{ color: template.text_color_card }">Adults</label>
                  <div class="qty-control" :style="{ borderColor: template.button_color }">
                    <span class="qty-btn" :style="{ color: template.button_color }">-</span>
                    <span class="qty-value" :style="{ color: template.text_color_card }">2</span>
                    <span class="qty-btn" :style="{ color: template.button_color }">+</span>
                  </div>
                </div>
                <div class="pricing-category">
                  <label class="category-label" :style="{ color: template.text_color_card }">Children</label>
                  <div class="qty-control" :style="{ borderColor: template.button_color }">
                    <span class="qty-btn" :style="{ color: template.button_color }">-</span>
                    <span class="qty-value" :style="{ color: template.text_color_card }">0</span>
                    <span class="qty-btn" :style="{ color: template.button_color }">+</span>
                  </div>
                </div>
              </div>

              <!-- Pricing summary -->
              <div class="pricing-summary">
                <span class="pricing-label" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">Subtotal</span>
                <span class="pricing-amount" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">$XX.XX</span>
                <small class="pricing-rate" :style="{ color: template.text_color_card }">$XX.XX per adult</small>

                <div v-if="template.show_total_pricing_on_events" class="pricing-total-section">
                  <span class="pricing-label" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">Total</span>
                  <span class="pricing-amount" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">$XX.XX</span>
                  <small class="pricing-rate" :style="{ color: template.text_color_card }">Tax: $X.XX</small>
                  <small class="pricing-rate" :style="{ color: template.text_color_card }">Fee: $X.XX</small>
                </div>
              </div>
            </div>
          </div>
          <div v-if="hasAddonsText" class="selected-game-card mt-2" :style="eventCardStyle">
            <div class="game-card-body">
              <h5 v-if="template.addons_title && template.addons_title.trim()" class="game-title" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">{{ template.addons_title }}</h5>
              <p v-if="template.addons_subtext && template.addons_subtext.trim()" class="game-description" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">{{ template.addons_subtext }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Customer info -->
      <div v-if="activeStep === 3" class="step-content">
        <div class="two-col-layout">
          <div class="main-col">
            <!-- Event upsell card (above_form) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'above_form'" class="sidebar-card" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-0 align-items-center">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
                <div class="payment-method-grid ml-auto my-0">
                  <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTabove_form' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
                </div>
              </div>
            </div>
            <!-- Personal information card -->
            <div class="form-card" :style="eventCardStyle">
              <h5 :style="{ fontFamily: template.font_family_card, color: template.link_color }">Personal information</h5>
              <div class="form-row-preview">
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">First name*</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Last name*</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
              </div>
              <div class="form-row-preview">
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Email*</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field" v-if="phoneFieldMode !== 'hidden'">
                  <span class="field-label" :style="{ color: template.link_color }">Phone{{ phoneFieldMode === 'required' ? '*' : '' }}</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
              </div>
              <template v-if="addressFieldMode !== 'hidden'">
                <div class="form-row-preview">
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">Address line 1{{ addressFieldMode === 'required' ? '*' : '' }}</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                </div>
                <div class="form-row-preview">
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">Address line 2</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                </div>
                <div class="form-row-preview">
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">City{{ addressFieldMode === 'required' ? '*' : '' }}</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">State{{ addressFieldMode === 'required' ? '*' : '' }}</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                </div>
                <div class="form-row-preview">
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">Zip{{ addressFieldMode === 'required' ? '*' : '' }}</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                  <div class="form-field">
                    <span class="field-label" :style="{ color: template.link_color }">Country{{ addressFieldMode === 'required' ? '*' : '' }}</span>
                    <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                  </div>
                </div>
              </template>
              <div class="form-row-preview" v-else-if="zipFieldMode !== 'hidden'">
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Zip code{{ zipFieldMode === 'required' ? '*' : '' }}</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field"></div>
              </div>
            </div>

            <!-- Additional information card -->
            <div v-if="activeCustomFields.length > 0" class="form-card" :style="eventCardStyle">
              <h5 :style="{ fontFamily: template.font_family_card, color: template.link_color }">Additional information</h5>
              <div v-for="cf in activeCustomFields" :key="'cf-' + cf.id" class="custom-field-row">
                <!-- Checkbox (type 1) -->
                <div v-if="cf.type && cf.type.id === 1" class="cf-checkbox">
                  <div class="cf-checkbox-box" :style="{ borderColor: template.button_color }"></div>
                  <span class="cf-checkbox-label" :style="{ color: template.link_color }">
                    {{ cf.label }}<template v-for="el in (cf.external_links || [])">
                      <span :key="'el-' + el.link" class="cf-link" :style="{ color: template.link_color }"> {{ el.link_text }}</span>
                    </template><template v-for="p in (cf.policies || [])">
                      <span :key="'p-' + p.policy_link_text" class="cf-link" :style="{ color: template.link_color }"> {{ p.policy_link_text }}</span>
                    </template><span v-if="cf.required">*</span>
                  </span>
                </div>
                <!-- Input field (type 2) -->
                <template v-if="cf.type && cf.type.id === 2">
                  <span class="field-label" :style="{ color: template.link_color }">{{ cf.label }}<span v-if="cf.required">*</span></span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </template>
                <!-- Dropdown (type 3) -->
                <template v-if="cf.type && cf.type.id === 3">
                  <span class="field-label" :style="{ color: template.link_color }">{{ cf.label }}<span v-if="cf.required">*</span></span>
                  <div class="field-dropdown" :style="{ borderColor: template.button_color }">
                    <span class="field-dropdown-text">Select</span>
                    <i class="el-icon-arrow-down"></i>
                  </div>
                </template>
              </div>
            </div>

            <!-- Back + Continue row -->
            <div class="nav-button-row">
              <span class="back-arrow">&#8592;</span>
              <div class="continue-button" :style="{ borderColor: template.button_color, color: template.button_color }">Continue</div>
            </div>
            <!-- Event upsell card (below_form) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'below_form'" class="sidebar-card" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-0 align-items-center">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
                <div class="payment-method-grid ml-auto my-0">
                  <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTbelow_form' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="sidebar-col">
            <div class="sidebar-card" :style="eventCardStyle">
              <div class="sidebar-add-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">Add another booking</div>
              <h5 class="sidebar-heading" :style="{ fontFamily: template.font_family_card, color: template.link_color }">Your purchases</h5>
              <div class="sidebar-booking-item">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Event name</b></span>
                  <span class="meta-line">Date: 07 Feb 2026</span>
                  <span class="meta-line">Time: 10:30</span>
                  <span class="meta-line">Qty: 2</span>
                </div>
              </div>
              <div v-if="showPromoOnInfoPreview" class="sidebar-promo">
                <div class="sidebar-promo-group">
                  <div class="sidebar-promo-input" :style="{ borderColor: template.button_color }">
                    <span>{{ template.promo_code_placeholder_text || 'Promo code' }}</span>
                  </div>
                  <div class="sidebar-promo-apply" :style="{ borderColor: template.button_color, color: template.button_color }">Apply</div>
                </div>
              </div>
              <div class="sidebar-summary">
                <div class="sidebar-divider" :style="{ backgroundColor: template.text_color_card }"></div>
                <div class="sidebar-line">
                  <span :style="{ color: template.link_color }">Subtotal</span>
                  <span :style="{ color: template.text_color_card }">$XX.XX</span>
                </div>
                <div class="sidebar-line">
                  <span :style="{ color: template.link_color }">Fee</span>
                  <span :style="{ color: template.text_color_card }">$X.XX</span>
                </div>
                <div class="sidebar-divider" :style="{ backgroundColor: template.text_color_card }"></div>
                <div class="sidebar-line sidebar-total">
                  <span :style="{ color: template.link_color }">Total</span>
                  <span :style="{ color: template.text_color_card }">$XX.XX</span>
                </div>
              </div>
            </div>
            <!-- Event upsell card (below_cart) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'below_cart'" class="sidebar-card mt-2" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-1">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
              </div>
              <div class="payment-method-grid mb-0">
                <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTbelow_cart' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 4: Payment/Checkout -->
      <div v-if="activeStep === 4" class="step-content">
        <div class="two-col-layout">
          <div class="main-col">
            <!-- Event upsell card (above_form) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'above_form'" class="sidebar-card" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-0 align-items-center">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
                <div class="payment-method-grid ml-auto my-0">
                  <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTabove_form' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
                </div>
              </div>
            </div>
            <div class="form-card" :style="eventCardStyle">
              <h5 :style="{ fontFamily: template.font_family_card, color: template.link_color }">Billing information</h5>

              <!-- Pay on arrival / deposit checkboxes -->
              <div class="billing-options">
                <div class="billing-option">
                  <div class="cf-checkbox-box" :style="{ borderColor: template.button_color }"></div>
                  <span :style="{ color: template.link_color }">Pay on arrival</span>
                </div>
                <div class="billing-option" v-if="depositPreviewLayout === 'checkbox'">
                  <div class="cf-checkbox-box" :style="{ borderColor: template.button_color }"></div>
                  <span :style="{ color: template.link_color }">Pay deposit</span>
                </div>
              </div>
              <p v-if="template.enable_deposit_text && template.deposit_text" class="deposit-preview-text"
                 :style="{ color: template.text_color_card, fontFamily: template.font_family_card }">{{ template.deposit_text }}</p>
              <div v-if="depositPreviewLayout === 'buttons'" class="payment-method-grid deposit-preview-buttons">
                <div class="pm-btn active" :style="{ backgroundColor: template.button_color, borderColor: template.button_color, color: '#fff' }">Pay in full</div>
                <div class="pm-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">Pay deposit</div>
              </div>
              <div v-if="depositPreviewLayout === 'cards'" class="deposit-preview-cards">
                <div class="deposit-preview-card" :style="{ borderColor: template.button_color, backgroundColor: template.button_color }">
                  <span style="color: #fff">Pay in full</span>
                </div>
                <div class="deposit-preview-card" :style="{ borderColor: template.button_color }">
                  <span :style="{ color: template.text_color_card }">Pay deposit</span>
                </div>
              </div>

              <!-- Payment method -->
              <span class="field-label" :style="{ color: template.link_color }">Payment method</span>
              <div class="payment-method-grid">
                <div class="pm-btn active" :style="{ backgroundColor: template.button_color, borderColor: template.button_color, color: '#fff' }">Card</div>
                <div class="pm-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">ACH</div>
                <div class="pm-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">Gift card</div>
                <div class="pm-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">Unpaid</div>
              </div>

              <!-- Name on card -->
              <span class="field-label" :style="{ color: template.link_color }">Name on card</span>
              <div class="form-row-preview">
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">First name</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Last name</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
              </div>

              <!-- Card provider selector -->
              <div class="card-provider-row">
                <div class="card-provider active" :style="{ borderColor: template.button_color, color: template.text_color_card }">Card</div>
                <div class="card-provider" :style="{ borderColor: template.button_color, color: template.text_color_card }">G Pay</div>
                <div class="card-provider" :style="{ borderColor: template.button_color, color: template.text_color_card }">Cash App</div>
                <div class="card-provider" :style="{ borderColor: template.button_color, color: template.text_color_card }">Klarna</div>
              </div>

              <!-- Card fields row -->
              <div class="form-row-preview card-fields-row">
                <div class="form-field" style="flex: 2">
                  <span class="field-label" :style="{ color: template.link_color }">Card number</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Expiry</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">CVC</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
              </div>

              <!-- Country + ZIP row -->
              <div class="form-row-preview">
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">Country</span>
                  <div class="field-dropdown" :style="{ borderColor: template.button_color }">
                    <span class="field-dropdown-text">United States</span>
                    <i class="el-icon-arrow-down"></i>
                  </div>
                </div>
                <div class="form-field">
                  <span class="field-label" :style="{ color: template.link_color }">ZIP code</span>
                  <div class="field-input" :style="{ borderColor: template.button_color }"></div>
                </div>
              </div>
            </div>
            <!-- Event upsell card (below_form) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'below_form'" class="sidebar-card" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-0 align-items-center">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
                <div class="payment-method-grid ml-auto my-0">
                  <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTbelow_form' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="sidebar-col">
            <div class="sidebar-card" :style="eventCardStyle">
              <div class="sidebar-add-btn" :style="{ borderColor: template.button_color, color: template.text_color_card }">Add another booking</div>
              <h5 class="sidebar-heading" :style="{ fontFamily: template.font_family_card, color: template.link_color }">Your purchases</h5>
              <div class="sidebar-booking-item">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Event name</b></span>
                  <span class="meta-line">Date: 07 Feb 2026</span>
                  <span class="meta-line">Time: 10:30</span>
                  <span class="meta-line">Qty: 2</span>
                </div>
              </div>
              <div v-if="template.show_promo_code_field" class="sidebar-promo">
                <div class="sidebar-promo-group">
                  <div class="sidebar-promo-input" :style="{ borderColor: template.button_color }">
                    <span>{{ template.promo_code_placeholder_text || 'Promo code' }}</span>
                  </div>
                  <div class="sidebar-promo-apply" :style="{ borderColor: template.button_color, color: template.button_color }">Apply</div>
                </div>
              </div>
              <div class="sidebar-summary">
                <div class="sidebar-divider" :style="{ backgroundColor: template.text_color_card }"></div>
                <div class="sidebar-line">
                  <span :style="{ color: template.link_color }">Subtotal</span>
                  <span :style="{ color: template.text_color_card }">$XX.XX</span>
                </div>
                <div class="sidebar-line">
                  <span :style="{ color: template.link_color }">Fee</span>
                  <span :style="{ color: template.text_color_card }">$X.XX</span>
                </div>
                <div class="sidebar-divider" :style="{ backgroundColor: template.text_color_card }"></div>
                <div class="sidebar-line sidebar-total">
                  <span :style="{ color: template.link_color }">Total</span>
                  <span :style="{ color: template.text_color_card }">$XX.XX</span>
                </div>
              </div>
            </div>
            <!-- Event upsell card (below_cart) -->
            <div v-if="showUpsellPreview && upsellPreviewPosition === 'below_cart'" class="sidebar-card mt-2" :style="eventCardStyle">
              <h5 class="sidebar-heading mb-1" :style="{ fontFamily: template.font_family_card, color: template.link_color }">{{ upsellPreviewTitle }}</h5>
              <div class="booking-meta mb-2" :style="{ color: template.text_color_card }">{{ upsellPreviewSubtext }}</div>
              <div class="sidebar-booking-item mb-1">
                <div class="booking-thumb"><i class="tim-icons icon-controller"></i></div>
                <div class="booking-meta" :style="{ color: template.link_color }">
                  <span class="meta-line"><b>Another event</b></span>
                  <span class="meta-line">From $XX.XX</span>
                  <span v-if="template.upsell_promo_code_id && template.upsell_badge_text" class="meta-line" :style="{ color: template.button_color }">{{ template.upsell_badge_text }}</span>
                </div>
              </div>
              <div class="payment-method-grid mb-0">
                <div class="pm-btn" v-for="t in ['09:00', '12:00', '13:30']" :key="'UPTbelow_cart' + t" :style="{ borderColor: template.button_color, color: template.button_color }">{{ t }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 5: Confirmation -->
      <div v-if="activeStep === 5" class="step-content">
        <div class="confirmation-preview">
          <div class="confirmation-card" :style="eventCardStyle">
            <!-- Confirmation message title -->
            <h4 class="confirmation-title" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
              {{ template.confirmation_message || 'Thank you for booking with us!' }}
            </h4>

            <hr class="confirmation-divider" :style="{ borderColor: template.text_color_card }">

            <!-- Booking details heading -->
            <h5 class="details-heading" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">Booking details</h5>

            <!-- Order reference (right-aligned) -->
            <div class="detail-line detail-right" :style="{ color: template.text_color_card, fontFamily: template.font_family_card }">
              Order reference: <b>#OTC-XXXXX</b>
            </div>

            <!-- Payment status (right-aligned) -->
            <div class="detail-line detail-right" :style="{ fontFamily: template.font_family_card, color: template.text_color_card }">
              Payment status: <span class="status-paid">Paid</span>
            </div>

            <!-- Sample booking item -->
            <div class="booking-detail-item" :style="{ color: template.text_color_card, fontFamily: template.font_family_card }">
              <span>Event name: 10:30, Fri 07 Feb 2026</span>
              <span>Quantity: 2</span>
            </div>

            <!-- Amount paid -->
            <div class="detail-line" :style="{ color: template.text_color_card, fontFamily: template.font_family_card }">
              Amount paid: $XX.XX
            </div>

            <!-- Navigation buttons -->
            <div class="confirmation-buttons">
              <div v-if="!template.auto_redirect" class="confirm-btn" :style="{ borderColor: template.button_color, color: template.button_color, fontFamily: template.font_family_card }">
                {{ template.redirect_button_text || 'Return to website' }}
              </div>
              <div v-if="template.display_waiver_link" class="confirm-btn" :style="{ borderColor: template.button_color, color: template.button_color, fontFamily: template.font_family_card }">
                Check in
              </div>
              <div v-if="template.auto_redirect" class="auto-redirect-notice" :style="{ color: template.text_color_card }">
                Redirecting in {{ template.redirect_delay || 15 }}s...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {Select, Option} from 'element-ui';
import { sanitizeQuillHtml } from '@/util/quillSanitize';

export default {
  name: 'SchematicPreview',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  props: {
    template: {
      type: Object,
      required: true
    },
    events: {
      type: Array,
      default: () => []
    },
    customFields: {
      type: Array,
      default: () => []
    },
    allGames: {
      type: Array,
      default: () => []
    },
    layoutMode: {
      type: String,
      default: 'default'
    },
    activeStep: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      maxDisplayEvents: 3,
      stepOptions: [
        { label: 'General settings' },
        { label: 'Event selection' },
        { label: 'Event details' },
        { label: 'Customer info' },
        { label: 'Checkout & payment' },
        { label: 'Confirmation' }
      ]
    }
  },

  computed: {
    showHeader() {
      if (this.activeStep === 0) return false
      return this.template.show_logo || this.template.title || this.template.subtext
    },

    // Event upsell card: mirrors the booking site's placement and English fallbacks
    showUpsellPreview() {
      return Boolean(this.template.show_upsell_section)
    },
    // Promo box on the Customer info preview step: hidden when the template
    // limits the field to the final checkout step
    showPromoOnInfoPreview() {
      return Boolean(this.template.show_promo_code_field) && this.template.promo_code_field_position !== 'final_step'
    },
    upsellPreviewPosition() {
      const position = this.template.upsell_position
      return position === 'below_form' || position === 'below_cart' ? position : 'above_form'
    },
    upsellPreviewTitle() {
      const t = this.template.upsell_title
      return t && t.trim() ? t : 'Add another event?'
    },
    upsellPreviewSubtext() {
      const t = this.template.upsell_subtext
      return t && t.trim() ? t : 'Other events available around your booking time.'
    },
    hasAddonsText() {
      const t = this.template
      return Boolean((t.addons_title && t.addons_title.trim()) || (t.addons_subtext && t.addons_subtext.trim()))
    },

    depositPreviewLayout() {
      return ['buttons', 'cards'].includes(this.template.deposit_layout_type) ? this.template.deposit_layout_type : 'checkbox'
    },

    activeEvents() {
      const gamesMap = new Map(this.allGames.map(g => [Number(g.id), g]))
      return this.events.filter(e => e.is_active == 1).map(e => {
        const fullGame = gamesMap.get(Number(e.game_id))
        const img = fullGame ? (fullGame.url || fullGame.img) : null
        return {
          ...e,
          img: img || 'https://console.offthecouch.io/img/placeholder.jpg',
          description: fullGame ? fullGame.description : ''
        }
      })
    },

    displayEvents() {
      return this.activeEvents.slice(0, this.maxDisplayEvents)
    },

    showEventMeta() {
      return this.template.show_game_duration ||
             this.template.show_number_of_players ||
             this.template.show_game_difficulty
    },

    canvasStyle() {
      return {
        backgroundColor: this.template.background_color,
        color: this.template.text_color,
        '--button-color': this.template.button_color,
        '--text-color': this.template.text_color
      }
    },

    eventCardStyle() {
      return {
        backgroundColor: this.template.card_color,
        color: this.template.text_color_card
      }
    },

    buttonStyle() {
      return {
        borderColor: this.template.button_color,
        color: this.template.button_color
      }
    },

    cardSlotStyle() {
      return {
        backgroundColor: 'transparent',
        color: this.template.button_color,
        borderColor: this.template.button_color,
        fontFamily: this.template.font_family_card
      }
    },

    infoTriggerStyle() {
      return {
        backgroundColor: this.template.button_color,
        color: this.template.card_color,
        fontFamily: this.template.font_family_card,
        ...this.infoTriggerPositionStyle
      }
    },

    infoTriggerPositionStyle() {
      const pos = this.template.game_info_trigger_position || 'bottom-right'
      const v = pos.startsWith('top') ? { top: '3px', bottom: 'auto' } : { bottom: '3px', top: 'auto' }
      const h = pos.endsWith('left') ? { left: '3px', right: 'auto' } : { right: '3px', left: 'auto' }
      return { ...v, ...h }
    },

    activeCustomFields() {
      return this.customFields.filter(f => f.active)
    },

    phoneFieldMode() {
      return this.template.phone_field_mode || 'required'
    },

    zipFieldMode() {
      return this.template.zip_field_mode || 'hidden'
    },

    addressFieldMode() {
      return this.template.address_field_mode || 'hidden'
    },

    previewEvent() {
      return this.activeEvents.length > 0 ? this.activeEvents[0] : null
    },

    layoutName() {
      const map = { 1: 'cards', 2: 'list', 3: 'calendar' }
      return map[this.template.layout_type_id] || 'cards'
    },

    listSlotStyle() {
      return {
        backgroundColor: 'transparent',
        borderColor: this.template.button_color,
        color: this.template.button_color,
        fontFamily: this.template.font_family_card
      }
    },

    listSlotDisabledStyle() {
      return {
        backgroundColor: 'transparent',
        borderColor: this.template.button_color,
        color: this.template.button_color,
        fontFamily: this.template.font_family_card,
        opacity: 0.45
      }
    },

    listThumbWidth() {
      const map = { 1: 36, 2: 48, 3: 64, 4: 80 }
      return map[this.template.list_image_size] || 48
    },

    listThumbHeight() {
      const map = { 1: 30, 2: 42, 3: 54, 4: 68 }
      return map[this.template.list_image_size] || 42
    },

    isListImageTop() {
      return this.template.list_image_position === 3
    }
  },

  methods: {
    sanitizeQuillHtml,
    truncateName(name) {
      if (!name) return 'Untitled event'
      return name.length > 18 ? name.substring(0, 18) + '...' : name
    }
  }
}
</script>

<style lang="scss" scoped>
.schematic-preview {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #171722;
  border-radius: 8px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #22233a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h4 {
      margin: 0;
      font-size: 1rem;
      font-weight: 400;
      color: #fff;
    }
  }

  &__canvas {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }
  }

}

.step-selector {
  width: 180px;

  ::v-deep .el-input__inner {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 11px;
    height: 28px;
    line-height: 28px;
  }

  ::v-deep .el-input__suffix {
    color: rgba(255, 255, 255, 0.5);
  }
}

// Schematic Modules
.schematic-module {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

// Site Header (matches CreateBooking.vue layout)
.site-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 12px 8px;

  img {
    max-width: 100%;
  }

  &__placeholder-icon {
    font-size: 24px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 4px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    margin-top: 8px;
  }

  &__subtext {
    font-size: 11px;
    opacity: 0.6;
    margin-top: 2px;
  }

  &__empty {
    font-size: 11px;
    opacity: 0.4;
  }
}

// Progress Bar Module
.module-progress {
  padding: 12px 16px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  .step-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);

    i {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &.active .step-icon {
    background: var(--button-color, #e14eca);

    i {
      color: var(--text-color, #fff);
    }
  }

  &.completed .step-icon {
    background: var(--button-color, #e14eca);
    opacity: 0.5;

    i {
      color: var(--text-color, #fff);
    }
  }

  .step-label {
    font-size: 8px;
    text-transform: uppercase;
    opacity: 0.6;
  }
}

.progress-line {
  flex: 1;
  height: 2px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 4px;

  &.filled {
    background: var(--button-color, #e14eca);
    opacity: 0.5;
  }
}

// Step content
.step-content {
  // No extra padding since canvas already has padding
}

// Date Navigation
.date-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  .nav-arrow {
    font-size: 9px;
    opacity: 0.6;
    cursor: pointer;
  }

  .date-picker {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    font-size: 10px;

    i {
      font-size: 12px;
      color: #e14eca;
    }
  }
}

// Events Grid Layout
.events-layout {
  padding: 12px 0;

  &.events-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

// List Board Layout
.events-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

// Calendar View Layout
.events-calendar {
  display: block;
}

.calendar-card {
  .calendar-card-row {
    display: flex;
    gap: 12px;
    padding: 12px;
  }

  .calendar-poster {
    flex-shrink: 0;
    width: 50px;
    height: 66px;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    i {
      font-size: 18px;
      opacity: 0.5;
    }
  }

  .calendar-main {
    flex: 1;
    min-width: 0;
  }

  .event-title {
    padding: 0 0 6px;
    font-size: 11px;
    font-weight: 600;
  }

  .calendar-weekdays,
  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 3px;
    max-width: 232px;
  }

  .calendar-weekdays {
    margin-bottom: 3px;

    span {
      text-align: center;
      font-size: 7px;
      font-weight: 600;
      opacity: 0.4;
    }
  }

  .calendar-day {
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 500;
    border-radius: 3px;
    background: rgba(125, 125, 125, 0.1);
    opacity: 0.85;

    &--muted {
      opacity: 0.3;
    }

    &--selected {
      font-weight: 700;
      opacity: 1;
    }
  }

  .calendar-slots {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    margin-top: 8px;
    max-width: 232px;

    .slot {
      padding: 4px 2px;
      text-align: center;
      border-radius: 3px;
      font-size: 7px;
      font-weight: 600;
      border: 1px solid;

      &.disabled {
        opacity: 0.45;
      }
    }
  }
}

.event-list-row {
  display: flex;
  align-items: stretch;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  min-height: 48px;
}

.event-list-row--img-right .event-list-thumb {
  border-radius: 0 6px 6px 0;
}

.event-list-left-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.event-list-left-col .event-list-thumb {
  border-radius: 6px 0 0 0;
}

.event-list-left-col .event-list-info {
  width: 100%;
  padding: 3px 6px;
}

.event-list-left-col + .event-list-slots {
  padding-left: 8px;
}

.event-list-thumb {
  flex-shrink: 0;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  i {
    font-size: 14px;
    opacity: 0.5;
  }

  &.gift-card-thumb {
    background: linear-gradient(135deg, rgba(255, 214, 0, 0.2), rgba(255, 152, 0, 0.2));

    i {
      color: #ffd600;
      opacity: 1;
      font-size: 14px;
    }
  }
}

.event-list-info {
  width: 30%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  min-width: 0;
}

.event-list-info-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.event-list-name {
  font-size: 9px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.game-info-trigger-inline {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 7px;
  margin-top: 2px;
  white-space: nowrap;
}

.event-list-meta {
  display: flex;
  gap: 6px;
  opacity: 0.6;

  .meta-item {
    font-size: 7px;
    display: flex;
    align-items: center;
    gap: 1px;
    white-space: nowrap;

    i {
      font-size: 8px;
    }
  }
}

.event-list-slots {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 6px 8px 6px 0;

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
  gap: 3px;
  align-content: center;

  .slot {
    padding: 4px 2px;
    text-align: center;
    border-radius: 3px;
    font-size: 7px;
    font-weight: 600;
    border: 1px solid;

    &.disabled {
      opacity: 0.45;
    }
  }
}

// Event Card with Embedded Slots
.event-card-schematic {
  border-radius: 6px;
  overflow: hidden;

  .event-image-placeholder {
    position: relative;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);

    .game-info-trigger-icon {
      position: absolute;
      bottom: 3px;
      right: 3px;
      font-size: 9px;
    }

    .game-info-trigger-chip {
      position: absolute;
      bottom: 3px;
      right: 3px;
      display: inline-flex;
      align-items: center;
      padding: 1px 5px;
      border-radius: 999px;
      font-size: 7px;
      line-height: 1.4;
      max-width: 80%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    i {
      font-size: 20px;
      opacity: 0.5;
    }

    &.gift-card-image {
      background: linear-gradient(135deg, rgba(255, 214, 0, 0.2), rgba(255, 152, 0, 0.2));

      i {
        color: #ffd600;
        opacity: 1;
      }
    }
  }

  .event-title {
    padding: 8px 8px 4px;
    font-size: 11px;
    font-weight: 600;
  }

  .event-meta {
    display: flex;
    gap: 8px;
    padding: 0 8px 6px;

    .meta-item {
      font-size: 9px;
      opacity: 0.6;
      display: flex;
      align-items: center;
      gap: 2px;

      i {
        font-size: 10px;
      }
    }
  }

  .embedded-slots {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    padding: 6px 8px;

    .slot {
      padding: 5px 2px;
      text-align: center;
      border-radius: 3px;
      font-size: 8px;
      font-weight: 500;
      border: 1px solid;

      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }

  .see-more-link {
    padding: 6px 8px;
    font-size: 9px;
    text-align: center;
    opacity: 0.6;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }

  .gift-card-description {
    padding: 0 8px 8px;
    font-size: 9px;
    opacity: 0.6;
  }

  .gift-card-button {
    margin: 0 8px 8px;
    padding: 6px;
    text-align: center;
    border: 1px solid;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 500;
  }
}

// Step 2: Booking Details
.booking-details-preview {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .selected-game-card {
    border-radius: 6px;
    overflow: hidden;
  }

  .game-image-top {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    i {
      font-size: 24px;
      opacity: 0.4;
    }
  }

  .game-card-body {
    padding: 10px;
  }

  .game-title {
    font-size: 12px;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .game-description {
    font-size: 9px;
    opacity: 0.7;
    margin: 0 0 8px;
    line-height: 1.3;
  }

  .game-datetime {
    font-size: 9px;
    margin-bottom: 6px;

    i {
      font-size: 10px;
    }

    .change-link {
      font-size: 8px;
      margin-left: 6px;
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .available-tickets {
    font-size: 8px;
    opacity: 0.7;
    margin-bottom: 8px;
  }

  .pricing-categories {
    margin-bottom: 10px;
  }

  .pricing-category {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;

    .category-label {
      font-size: 9px;
      text-transform: uppercase;
      font-weight: 500;
      margin: 0;
    }
  }

  .qty-control {
    display: flex;
    align-items: center;
    border: 1px solid;
    border-radius: 3px;

    .qty-btn {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      cursor: pointer;
    }

    .qty-value {
      width: 24px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 600;
      border-left: 1px solid rgba(255, 255, 255, 0.1);
      border-right: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  .pricing-summary {
    display: flex;
    flex-direction: column;
  }

  .pricing-label {
    font-size: 8px;
    text-transform: uppercase;
    font-weight: 500;
    margin-bottom: 2px;
  }

  .pricing-amount {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 2px;
  }

  .pricing-rate {
    font-size: 8px;
    opacity: 0.6;
    margin-bottom: 2px;
  }

  .pricing-total-section {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
  }
}

// Shared form styles for Step 3 & 4
.form-card {
  padding: 10px;
  border-radius: 6px;

  h5 {
    font-size: 11px;
    font-weight: 600;
    margin: 0 0 10px;
  }
}

.form-row-preview {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.form-field {
  flex: 1;

  .field-label {
    display: block;
    font-size: 8px;
    margin-bottom: 3px;
  }

  .field-input {
    height: 22px;
    background: transparent;
    border: 1px solid;
    border-radius: 3px;
  }

  .field-dropdown {
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 6px;
    background: transparent;
    border: 1px solid;
    border-radius: 3px;
    font-size: 8px;

    i {
      font-size: 7px;
    }
  }
}

// Two-column layout for Steps 3 & 4
.two-col-layout {
  display: flex;
  gap: 8px;

  .main-col {
    flex: 3;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .sidebar-col {
    flex: 2;
    min-width: 0;
  }
}

// Sidebar card
.sidebar-card {
  padding: 8px;
  border-radius: 6px;

  .sidebar-add-btn {
    padding: 4px;
    text-align: center;
    border: 1px solid;
    border-radius: 3px;
    font-size: 7px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .sidebar-heading {
    font-size: 9px;
    font-weight: 600;
    margin: 0 0 6px;
  }

  .sidebar-booking-item {
    display: flex;
    gap: 6px;
    margin-bottom: 8px;
  }

  .booking-thumb {
    width: 26px;
    height: 26px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 11px;
      opacity: 0.5;
    }
  }

  .booking-meta {
    display: flex;
    flex-direction: column;
    font-size: 7px;
    line-height: 1.4;

    .meta-line {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .sidebar-promo {
    margin-bottom: 6px;
  }

  .sidebar-promo-group {
    display: flex;

    .sidebar-promo-input {
      flex: 1;
      height: 18px;
      display: flex;
      align-items: center;
      padding: 0 4px;
      background: transparent;
      border: 1px solid;
      border-radius: 3px 0 0 3px;
      border-right: none;
      font-size: 7px;
      opacity: 0.5;
    }

    .sidebar-promo-apply {
      height: 18px;
      display: flex;
      align-items: center;
      padding: 0 5px;
      border: 1px solid;
      border-radius: 0 3px 3px 0;
      font-size: 7px;
      font-weight: 600;
      text-transform: uppercase;
      white-space: nowrap;
    }
  }

  .sidebar-summary {
    .sidebar-line {
      display: flex;
      justify-content: space-between;
      font-size: 7px;
      padding: 2px 0;

      &.sidebar-total {
        font-weight: 600;
      }
    }

    .sidebar-divider {
      height: 1px;
      opacity: 0.2;
      margin: 4px 0;
    }
  }
}

// Step 3: Customer Info
.nav-button-row {
  display: flex;
  align-items: center;
  gap: 8px;

  .back-arrow {
    font-size: 12px;
    opacity: 0.6;
  }

  .continue-button {
    flex: 1;
    padding: 6px;
    text-align: center;
    border: 1px solid;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.custom-field-row {
  margin-bottom: 8px;
}

.cf-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 6px;

  .cf-checkbox-box {
    width: 12px;
    height: 12px;
    border: 1px solid;
    border-radius: 2px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .cf-checkbox-label {
    font-size: 8px;
    line-height: 1.4;
  }

  .cf-link {
    text-decoration: underline;
  }
}

// Step 4: Billing
.billing-options {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;

  .billing-option {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 8px;

    .cf-checkbox-box {
      width: 12px;
      height: 12px;
      border: 1px solid;
      border-radius: 2px;
      flex-shrink: 0;
    }
  }
}

.deposit-preview-text {
  font-size: 7px;
  font-style: italic;
  opacity: 0.85;
  margin: 0 0 6px;
}

.deposit-preview-buttons {
  margin-top: 0;
}

.deposit-preview-cards {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 10px;

  .deposit-preview-card {
    border: 1px solid;
    border-radius: 3px;
    padding: 4px 7px;
    font-size: 7px;
    font-weight: 500;
  }
}

.payment-method-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 5px 0 10px;

  .pm-btn {
    padding: 3px 7px;
    border: 1px solid;
    border-radius: 3px;
    font-size: 7px;
    font-weight: 500;
    text-align: center;
  }
}

.card-provider-row {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;

  .card-provider {
    flex: 1;
    padding: 3px 2px;
    border: 1px solid;
    border-radius: 3px;
    font-size: 7px;
    text-align: center;

    &.active {
      border-width: 2px;
    }
  }
}

// Step 0: General settings
.general-settings-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
}

.settings-section-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
  margin-bottom: 10px;
}

.color-swatches-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.color-swatch-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.color-swatch-label {
  font-size: 9px;
  opacity: 0.7;
}

.font-preview-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.font-preview-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.font-preview-label {
  font-size: 9px;
  opacity: 0.6;
}

.font-preview-name {
  font-size: 11px;
  font-weight: 500;
}

// Step 5: Confirmation
.confirmation-preview {
  display: flex;
  justify-content: center;

  .confirmation-card {
    padding: 12px;
    border-radius: 6px;
    width: 100%;
    max-width: 320px;
  }

  .confirmation-title {
    font-size: 12px;
    font-weight: 600;
    margin: 0 0 8px;
    text-align: center;
  }

  .confirmation-divider {
    border: none;
    border-top: 1px solid;
    opacity: 0.2;
    margin: 8px 0;
  }

  .details-heading {
    font-size: 11px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  .detail-line {
    font-size: 9px;
    margin-bottom: 4px;

    &.detail-right {
      text-align: right;
    }
  }

  .status-paid {
    color: #00d26a;
    text-transform: uppercase;
    font-weight: 600;
  }

  .booking-detail-item {
    display: flex;
    flex-direction: column;
    font-size: 9px;
    margin: 8px 0;
    gap: 2px;
  }

  .confirmation-buttons {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .confirm-btn {
    padding: 6px;
    text-align: center;
    border: 1px solid;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 500;
    width: 100%;
  }

  .auto-redirect-notice {
    text-align: center;
    font-size: 8px;
    opacity: 0.5;
    padding: 4px 0;
  }
}

// Light mode overrides
.white-content {
  .schematic-preview {
    background: #f5f6fa;

    &__header {
      background: #eceef5;
      border-bottom-color: rgba(0, 0, 0, 0.08);

      h4 {
        color: #344675;
      }
    }
  }

  .step-selector {
    ::v-deep .el-input__inner {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.12);
      color: #344675;
    }

    ::v-deep .el-input__suffix {
      color: rgba(0, 0, 0, 0.4);
    }
  }

  .schematic-module {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.08);
  }

  .settings-section {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.08);
  }

  .settings-section-title {
    color: #344675;
  }

  .color-swatch {
    border-color: rgba(0, 0, 0, 0.15);
  }

  .color-swatch-label {
    color: rgba(0, 0, 0, 0.6);
  }

  .font-preview-item {
    background: rgba(0, 0, 0, 0.04);
  }

  .font-preview-label {
    color: rgba(0, 0, 0, 0.5);
  }

  .font-preview-name {
    color: #344675;
  }

  .progress-step .step-icon {
    background: rgba(0, 0, 0, 0.08);

    i {
      color: rgba(0, 0, 0, 0.4);
    }
  }

  .progress-line {
    background: rgba(0, 0, 0, 0.12);
  }

  .progress-step .step-label {
    color: rgba(0, 0, 0, 0.5);
  }

  .site-header__placeholder-icon {
    color: rgba(0, 0, 0, 0.25);
  }

  .date-navigation {
    background: rgba(0, 0, 0, 0.04);

    .date-picker {
      background: rgba(0, 0, 0, 0.03);
      border-color: rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
